import React, { useEffect, useContext, useState } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Building,
  CalendarCheck,
  CurrencyDollar,
  CheckCircle,
  Clock,
  Wrench,
} from "react-bootstrap-icons";
import "../../assets/css/vendors/HotelScreens/hotelDashboardscreen.css";
import VendorLayout from "../../layout/HotelVendorLayout/VendorLayout";
import DashboardCard from "../../components/vendors/DashboardCard";
import axios from "axios";
import AppContext from "../../context/AppContext";
import { useNavigate } from "react-router-dom";

const HotelDashboardscreen = () => {
  const { baseUrl, authToken, vendorId } = useContext(AppContext);
  const navigate = useNavigate();
  const [totalRooms, setTotalRooms] = useState(0);
  const [availableRooms, setAvailableRooms] = useState(0);
  const [bookedRooms, setBookedRooms] = useState(0);
  const [maintenanceRooms, setMaintenanceRooms] = useState(0);
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("Dashboard mounted, token:", authToken);
    if (!authToken) {
      navigate("/auth/login");
    }
  }, [authToken, navigate]);

  useEffect(() => {
    const fetchDashboardStats = () => {
      setLoading(true);

      axios
        .get(`${baseUrl}/vendor/getDashboardStats`, {
          params: { vendor_id: vendorId },
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          if (response.data.success) {
            const d = response.data.data;
            setTotalRooms(d.total_rooms);
            setAvailableRooms(d.available_rooms);
            setBookedRooms(d.booked_rooms);
            setMaintenanceRooms(d.maintenance_rooms);
            setTotalBookings(d.total_bookings);
            setTotalRevenue(d.total_revenue || 0);
          }
        })
        .catch((error) => {
          console.error("Dashboard stats fetch error:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    if (authToken && vendorId) {
      fetchDashboardStats();
    }
  }, [authToken, vendorId, baseUrl]);
  const statCardsData = [
    {
      title: "Total Rooms",
      value: totalRooms,               
      badgeText: `This Month`,
      icon: <Building size={24} />,
      cardClass: "hotel-db-primary",
      iconBg: "hotel-db-bg-primary",
      iconColor: "hotel-db-text-white",
      additionalInfo: `${availableRooms} rooms available today`,  
      isCurrency: false,
    },
    {
      title: "Total Bookings",
      value: totalBookings,           
      badgeText: `This Month`,
      icon: <CalendarCheck size={24} />,
      cardClass: "hotel-db-success",
      iconBg: "hotel-db-bg-success",
      iconColor: "hotel-db-text-white",
      additionalInfo: "",
      isCurrency: false,
    },
   {
  title: "Total Revenue",
  value: totalRevenue,
  badgeText: "All Time",
  icon: <CurrencyDollar size={24} />,
  cardClass: "hotel-db-warning",
  iconBg: "hotel-db-bg-warning",
  iconColor: "hotel-db-text-white",
  additionalInfo: "",
  isCurrency: true,
},
  ];

  const roomStatusData = [
    {
      name: "Available",
      value: availableRooms,          
      color: "#4CAF50",
      icon: <CheckCircle size={16} />,
    },
    {
      name: "Booked",
      value: bookedRooms,              
      color: "#2C5F7C",
      icon: <Clock size={16} />,
    },
    {
      name: "Maintenance",
      value: maintenanceRooms,         
      color: "#FFB800",
      icon: <Wrench size={16} />,
    },
  ];

  const renderCustomLabel = ({ name, value }) => {
    if (value > 0) {
      return `${name}: ${value}`;
    }
    return null;
  };

  return (
    <VendorLayout>
      <div className="hotel-dashboard-screen">

        <div className="dashboard-header-section">
          <div className="dashboard-header-content">
            <h1 className="dashboard-main-title">Hotel Dashboard</h1>
            <p className="dashboard-main-subtitle">
              Overview of your hotel performance and statistics
            </p>
          </div>
          <div className="dashboard-date-section">
            <span className="dashboard-current-date">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        <Row className="dashboard-stats-row">
          {statCardsData.map((stat, idx) => (
            <Col xl={4} lg={4} md={12} className="mb-4" key={idx}>
              <DashboardCard
                title={stat.title}
                value={stat.value}
                badgeText={stat.badgeText}
                icon={stat.icon}
                cardClass={stat.cardClass}
                iconBg={stat.iconBg}
                iconColor={stat.iconColor}
                additionalInfo={stat.additionalInfo}
                isCurrency={stat.isCurrency}
              />
            </Col>
          ))}
        </Row>

        <Row className="dashboard-charts-row">
          <Col lg={12}>
            <Card className="hotel-chart-card">
              <Card.Body className="chart-card-body">

                <div className="chart-header-section">
                  <h5 className="chart-main-title">Room Status Distribution</h5>
                  <p className="chart-main-subtitle">
                    Current occupancy status of all rooms
                  </p>
                </div>

                <div className="chart-wrapper-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={roomStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={120}
                        paddingAngle={2}
                        dataKey="value"
                        label={renderCustomLabel}
                        labelLine={false}
                      >
                        {roomStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value} rooms`, "Count"]}
                        labelFormatter={(label) => `Status: ${label}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-container">
                  {roomStatusData.map((item, index) => (
                    <div key={index} className="hotel-legend-item">
                      <div
                        className="legend-color-indicator"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <div className="legend-content-info">
                        <span className="legend-name-text">{item.name}</span>
                        <span className="legend-count-text">
                          {item.value} rooms
                        </span>
                      </div>
                      <div className="legend-icon-wrapper">{item.icon}</div>
                    </div>
                  ))}
                </div>

              </Card.Body>
            </Card>
          </Col>
        </Row>

      </div>
    </VendorLayout>
  );
};

export default HotelDashboardscreen;